/**
 * Kubernetes resource quantities, so a visitor can paste the values straight from a
 * manifest: `250m`, `0.5`, `512Mi`, `1.5Gi`, `2G`.
 *
 * Internally CPU is millicores and memory is MiB — the units kubelet reports
 * allocatable in, and the ones every reservation formula below is written in.
 * https://kubernetes.io/docs/reference/kubernetes-api/common-definitions/quantity/
 */

const BYTES_PER_MIB = 1024 * 1024

/** Suffix → MiB. Binary suffixes are powers of 1024, decimal ones powers of 1000. */
const MEMORY_SUFFIXES: Readonly<Record<string, number>> = {
  Ki: 1 / 1024,
  Mi: 1,
  Gi: 1024,
  Ti: 1024 * 1024,
  k: 1e3 / BYTES_PER_MIB,
  M: 1e6 / BYTES_PER_MIB,
  G: 1e9 / BYTES_PER_MIB,
  T: 1e12 / BYTES_PER_MIB,
}

const QUANTITY = /^([0-9]*\.?[0-9]+)\s*([A-Za-z]*)$/

function split(text: string): { value: number; suffix: string } | null {
  const match = QUANTITY.exec(text.trim())
  if (!match) return null
  const value = Number(match[1])
  return Number.isFinite(value) ? { value, suffix: match[2] ?? '' } : null
}

/** `250m` or `0.25` → 250. Returns `null` for anything that is not a CPU quantity. */
export function parseCpu(text: string): number | null {
  const parts = split(text)
  if (!parts) return null
  if (parts.suffix === 'm') return parts.value
  if (parts.suffix === '') return parts.value * 1000
  return null
}

/**
 * `512Mi`, `1Gi` or `2G` → MiB. A bare number is read as MiB rather than bytes, as
 * Kubernetes would: nobody sizing a pod means 512 bytes, and the field says so.
 */
export function parseMemory(text: string): number | null {
  const parts = split(text)
  if (!parts) return null
  if (parts.suffix === '') return parts.value
  const factor = MEMORY_SUFFIXES[parts.suffix]
  return factor === undefined ? null : parts.value * factor
}

/** Shortest exact quantity for a CPU amount: `2` for whole cores, else millicores. */
export function formatCpu(millicores: number): string {
  if (millicores % 1000 === 0) return String(millicores / 1000)
  return `${Number(millicores.toFixed(3))}m`
}

/** `Gi` when the amount is whole gibibytes, else `Mi`. */
export function formatMemory(mib: number): string {
  if (mib !== 0 && mib % 1024 === 0) return `${mib / 1024}Gi`
  return `${Number(mib.toFixed(3))}Mi`
}
